import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import classnames from 'classnames';

import { screen, colors } from 'components/styles/variables';
import { trackHomePageFeaturedSearch } from 'lib/Tracking';

export interface PriceCardProps {
  price: string;
  image: string;
}

const PriceCard = ({ price, image }: PriceCardProps): JSX.Element => {
  const { t } = useTranslation();
  const [viewRef, inView] = useInView({ triggerOnce: true });

  const linkList = [
    {
      link: `/pickup+truck-under+${price.replace(/,/g, '')}`,
      i18nKey: 'home.prices.pickupsUnder',
    },
    {
      link: `/suv-under+${price.replace(/,/g, '')}`,
      i18nKey: 'home.prices.suvsUnder',
    },
    {
      link: `/minivan-under+${price.replace(/,/g, '')}`,
      i18nKey: 'home.prices.miniVansUnder',
    },
    {
      link: `/under+${price.replace(/,/g, '')}`,
      i18nKey: 'home.prices.seeAllUnder',
    },
  ];

  const cardTitle = t('home.prices.underPrice', { price });
  const cardUrl = `/under+${price.replace(/,/g, '')}`;

  return (
    <li className="price-card" ref={viewRef}>
      <a
        href={cardUrl}
        onClick={(): void => trackHomePageFeaturedSearch('featured_prices', cardUrl)}
      >
        <div
          aria-label={t('home.prices.imgAlt', { price })}
          className={classnames('price-image-container', {
            'price-image-container-view': inView,
          })}
          role="img"
        >
          <h3>{cardTitle}</h3>
        </div>
      </a>
      <ul className="price-link-list">
        {linkList.map(({ link, i18nKey }) => {
          const title = t(i18nKey, { price });
          return (
            <PriceCardItem key={link} link={link}>
              {title}
            </PriceCardItem>
          );
        })}
      </ul>

      <style jsx>
        {`
          .price-card {
            width: 150px;
            margin: 10px;
          }

          .price-image-container {
            width: 150px;
            height: 47px;
            border-radius: 6px;
            background-size: cover;
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: 10px;
          }

          .price-image-container-view {
            background: url(${image}) repeat black;
          }

          h3 {
            margin: 0;
            width: 150px;
            height: 47px;
            border-radius: 6px;
            background-color: rgba(0, 0, 0, 0.5);
            color: white;
            text-align: center;
            line-height: 47px;
            letter-spacing: 0.2px;
            font-size: 13px;
            text-transform: uppercase;
          }

          h3:hover {
            background-color: rgba(0, 0, 0, 0.25);
          }

          .price-link-list {
            list-style: none;
            padding: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          @media (min-width: ${screen.smMin}) {
            .price-card {
              width: 256px;
              margin: 15px;
              box-sizing: border-box;
              border-radius: 6px;
              border: 1px solid #ccc;
              overflow: hidden;
            }
            .price-image-container {
              width: 100%;
              height: 88px;
              border-radius: unset;
            }

            .price-link-list {
              padding-bottom: 5px;
            }

            h3 {
              width: 256px;
              height: 88px;
              line-height: 88px;
              font-size: 16px;
              border-radius: unset;
            }
          }

          @media (min-width: ${screen.mdMin}) {
            .price-card {
              width: 208px;
              margin: 13px;
            }
            .price-image-container {
              height: 71px;
            }

            .price-link-list li a {
              font-size: 12px;
            }

            h3 {
              width: 208px;
              height: 71px;
              line-height: 71px;
              font-size: 14px;
            }
          }

          @media (min-width: ${screen.lgMin}) {
            .price-card {
              width: 256px;
            }
            .price-image-container {
              height: 88px;
            }

            h3 {
              width: 256px;
              height: 88px;
              line-height: 88px;
              font-size: 16px;
            }

            .price-link-list li a {
              font-size: 14px;
              font-weight: 600;
            }
          }

          @media (max-width: 360px) {
            .price-card {
              margin: 5px;
            }
          }
          @media (max-width: 320px) {
            .price-card {
              margin: 2px;
            }
          }
        `}
      </style>
    </li>
  );
};

export interface PriceCardItemInterface {
  link: string;
  children: ReactNode;
}

const PriceCardItem = ({ link, children }: PriceCardItemInterface): JSX.Element => (
  <li>
    <a
      href={link}
      onClick={(): void => {
        trackHomePageFeaturedSearch('featured_prices', link);
      }}
    >
      {children}
    </a>
    <style jsx>{`
      li {
        margin-bottom: 10px;
      }

      li a {
        text-transform: none;
        color: black;
        font-size: 12px;
        font-weight: 600;
      }

      li a:hover {
        color: ${colors.ALR1};
      }

      li:last-child a {
        text-transform: uppercase;
        color: ${colors.G3};
        font-size: 11px;
        font-weight: bold;
        letter-spacing: 0.3px;
      }
      li:last-child a:hover {
        color: ${colors.ALR1};
      }

      @media (min-width: ${screen.smMin}) {
        li {
          margin-bottom: 5px;
        }

        li a {
          font-size: 14px;
        }
      }

      @media (min-width: ${screen.mdMin}) {
        li a {
          font-size: 12px;
        }
        li {
          margin-bottom: 10px;
        }
      }
      @media (min-width: ${screen.lgMin}) {
        li a {
          font-size: 14px;
          font-weight: 600;
        }

        li:last-child a {
          font-size: 12px;
        }
      }
    `}</style>
  </li>
);

export default PriceCard;
